// External libraries
import * as React from "react";
import { observer } from "mobx-react";
import { useDialog } from "@shared-ui/dialog-context";
import { Experiment as Experiments, isVariantEnabled } from "components/utility/experiment";
import { useLocalization } from "@shared-ui/localization-context";
import { withStores } from "stores";

// UITK
import { UitkLinkButton } from "uitk-react-button";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkFigure } from "uitk-react-images";
import { UitkText } from "uitk-react-text";

// Types
import { ModuleMapProps } from "./typings";
import { MapsFlexModuleResult } from "typings/microserviceModels/maps-flex-module";

// Components
import { useFullscreenOpen } from "./hooks";
import { MapContext } from "./MapContext";
import { LazyLoadedImage } from "src/components/utility/LazyLoadedImage/LazyLoadedImage";

const EGMapsModule = React.lazy(() => import(/* webpackChunkName: "module-map" */ "./EGMapsModule"));

/**
 * Wrapper Map Component. Here we inject the stores and is rendered the call to action button
 * and is rendered the Main Map Component.
 * @param {MapsProps} props
 */
export const EGMaps = withStores(
  "analytics",
  "hotels",
  "staticMap",
  "flexModuleModelStore"
)(
  observer((props: ModuleMapProps) => {
    const { flexTemplateNode, context, hotels: hotelsStore, staticMap, flexModuleModelStore } = props;
    const { formatText } = useLocalization();
    const [isClient, setIsClient] = React.useState(false);
    const { id, options } = flexTemplateNode;
    const [isDialogOpen, dialogActions, MapDialogComponent] = useDialog(`open-eg-${id}`);
    const { sendUserToSRP, toggleShowFullscreenMap } = useFullscreenOpen(props, isDialogOpen, dialogActions);

    React.useEffect(() => {
      setIsClient(true);
    }, []);

    const model = flexModuleModelStore.getModel(id) as MapsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const location = model.location ?? props.context.searchContext.location;
    const { Blossom_SRP_Lite_V2 } = Experiments;
    const ctaText = isVariantEnabled(context, Blossom_SRP_Lite_V2)
      ? formatText("maps.callToActionButtonViewInAMap")
      : formatText("maps.callToActionButtonShowMap");

    return (
      <div
        className="Maps uitk-cell all-cell-1-1 uitk-cell all-y-padding-three"
        id={id}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        <UitkLinkButton
          onClick={toggleShowFullscreenMap}
          disabled={!hotelsStore.allHotelsOnMap.length}
          className="uitk-grid uitk-cell uitk-map-call-to-action"
        >
          <UitkCard>
            <UitkFigure>
              <LazyLoadedImage
                alt={formatText("maps.hotel.multipleHotels.a11yLabel", location?.name)}
                src={staticMap.signedUrl}
                offset={100}
              />
            </UitkFigure>
            <UitkCardContentSection>
              <UitkText size={300} className="uitk-type-center uitk-button-old-link">
                {ctaText}
              </UitkText>
            </UitkCardContentSection>
          </UitkCard>
        </UitkLinkButton>
        <MapContext.Provider
          value={{
            toggleShowFullscreenMap,
            sendUserToSRP,
            shouldOpenMapInSRP: false,
          }}
        >
          <MapDialogComponent>
            {isClient && (
              <React.Suspense fallback={<div />}>
                <EGMapsModule {...props} />
              </React.Suspense>
            )}
          </MapDialogComponent>
        </MapContext.Provider>
      </div>
    );
  })
);

EGMaps.displayName = "EGMaps";

export default EGMaps;
